import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { FullPageModal } from "component/shared/full-page-modal";
import { Manual } from "component/page/market/criteria/manual";
import { Criterion } from "model/criterion";
import { bindActionCreators, Dispatch } from "redux";
import { createMarket } from "redux/slice/markets";
import { fetchMarketReports } from "redux/slice/market-reports";
import { connect } from "react-redux";
import * as Router from "react-router-dom";
import { RootState, getPayload } from "redux/store";
import { urls } from "routes/urls";
import { LoadingIndicator } from "component/shared/loading-indicator";
import { User } from "model/user";
import { ListingSearch } from "component/shared/listing-search";
import { fetchMarketListings } from "redux/slice/market-listings";
import { styles } from "./styles"
import * as env from "shared/env";

interface OwnProps {
	user: User;
	closeAddMarketDialog: () => void;
	helperTextStyle?: string;
}

interface Props extends 
	OwnProps, 
	ReturnType<typeof mapDispatchToProps>,
	ReturnType<typeof mapStateToProps>,
	WithStyles<typeof styles>,
	Router.RouteComponentProps
{
}

interface State {
	name: string;
	type: "search" | "manual";
	criteria: Criterion[];
	saved: boolean;
	errorText: string;
	listingsTotal?: number;
	lowResultsDialogOpen: boolean;
	isTermNonActiveListing: boolean;
	pendingOrSoldDialogOpen: boolean;
}

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	createMarket,
	fetchMarketReports,
	fetchMarketListings
}, dispatch);

const mapStateToProps = (state: RootState) => {
	return {
		error: state.markets.error,
	};
};

class Component extends React.Component<Props, State> {
	
	constructor(props: Props) {
		super(props);
		this.state = {
			name: "",
			type: "search",
			criteria: [],
			saved: false,
			errorText: "",
			lowResultsDialogOpen: false,
			isTermNonActiveListing: false,
			pendingOrSoldDialogOpen: false,
		}
	}

	private toggleSearchType() {
		const type = this.state.type === "search" ? "manual" : "search"
		this.setState({type, criteria: [], isTermNonActiveListing: false, pendingOrSoldDialogOpen: false});
	}

	private hasAddressTerm(): boolean {
		const { criteria } = this.state;
		return criteria.some(c => c.name === "term" && !!c.value);
	}

	private handleSaveClick() {
		const { name, listingsTotal, isTermNonActiveListing } = this.state;
		if (!name) {
			this.setState({errorText: "Please provide a name for the market"});
			return;
		}
		if (isTermNonActiveListing) {
			this.setState({ pendingOrSoldDialogOpen: true });
			return;
		}
		if (this.hasAddressTerm() && listingsTotal !== undefined && listingsTotal < 3) {
			this.setState({ lowResultsDialogOpen: true });
			return;
		}
		this.persistMarket();
	}

	private async persistMarket() {
		const { name, criteria } = this.state;
		const { createMarket, history, fetchMarketListings, fetchMarketReports } = this.props;

		this.setState({saved: true});
		const marketResource = getPayload(await createMarket({ market: {
			name,
			criteria,
		}}));
		await fetchMarketReports();
		await fetchMarketListings();
		history.push(urls.market(marketResource));
	}
	
	public render() {
		const { closeAddMarketDialog, helperTextStyle, classes = {}, user, error } = this.props;
		const { type, name, saved, errorText } = this.state;
		const actions = !saved ? (
			<>
				<Mui.Button
					variant="outlined"
					color="inherit"
					onClick={() => closeAddMarketDialog()}
				>
					Cancel
				</Mui.Button>
				<Mui.Button
					disabled={!name}
					variant="contained"
					color="primary"
					onClick={() => this.handleSaveClick()}
				>
					Add Market
				</Mui.Button>
			</>
		) : undefined;

		return (
			<>
			<Mui.Dialog
				open={this.state.pendingOrSoldDialogOpen}
				onClose={() => this.setState({ pendingOrSoldDialogOpen: false })}
			>
				<Mui.DialogTitle>Pending or sold listings can't be saved</Mui.DialogTitle>
				<Mui.DialogContent>
					<Mui.DialogContentText>
						Markets only track active listings. Try broadening your search — adjusting the location, price, or bed count may surface similar active homes nearby.
					</Mui.DialogContentText>
				</Mui.DialogContent>
				<Mui.DialogActions>
					<Mui.Button
						variant="contained"
						color="primary"
						onClick={() => this.setState({ pendingOrSoldDialogOpen: false })}
					>
						Update Criteria
					</Mui.Button>
				</Mui.DialogActions>
			</Mui.Dialog>
			<Mui.Dialog
				open={this.state.lowResultsDialogOpen}
				onClose={() => this.setState({ lowResultsDialogOpen: false })}
			>
				<Mui.DialogTitle>Limited search results</Mui.DialogTitle>
				<Mui.DialogContent>
					<Mui.DialogContentText>
						This search currently has very few matching listings. Expanding your criteria could help include more homes for this market.
					</Mui.DialogContentText>
				</Mui.DialogContent>
				<Mui.DialogActions>
					<Mui.Button
						onClick={() => this.setState({ lowResultsDialogOpen: false })}
					>
						Update Criteria
					</Mui.Button>
					<Mui.Button
						variant="contained"
						color="primary"
						onClick={() => {
							this.setState({ lowResultsDialogOpen: false });
							this.persistMarket();
						}}
					>
						Add Market Anyway
					</Mui.Button>
				</Mui.DialogActions>
			</Mui.Dialog>
			<FullPageModal open onClose={() => closeAddMarketDialog()} title="Add Market" actions={actions}>
				{saved ? (
					<Mui.Grid
						container
						direction="column"
						justifyContent="center"
						alignContent="center"
						className={classes.loadingContainer}
					>
						<Mui.Grid item>
							<LoadingIndicator />
						</Mui.Grid>
						<Mui.Grid item>
							{error ? "Error" : "Saving..."}
						</Mui.Grid>
					</Mui.Grid>
				) : (
					<Mui.Grid container direction="column" spacing={2}>
						<Mui.Grid container item spacing={2}>
							<Mui.Grid item xs={6}>
								<Mui.TextField
									value={name}
									variant="outlined"
									label="Market Name*"
									fullWidth
									onChange={(e) => this.setState({ name: e.currentTarget.value })}
									helperText={!name && errorText}
									FormHelperTextProps={{ className: helperTextStyle }}
									inputProps={{ maxLength: 256 }}
								/>
							</Mui.Grid>
							<Mui.Grid item xs={6}>
								<Mui.FormControl fullWidth variant="outlined">
									<Mui.InputLabel>Type</Mui.InputLabel>
									<Mui.Select
										label="Type"
										value={type}
										onChange={() => this.toggleSearchType()}
									>
										<Mui.MenuItem value="search">Search</Mui.MenuItem>
										<Mui.MenuItem value="manual">Manual (add listing by MLS #)</Mui.MenuItem>
									</Mui.Select>
								</Mui.FormControl>
							</Mui.Grid>
						</Mui.Grid>
						<Mui.Grid item>
							{type === "manual" ? (
								<Manual onUpdate={(criterion) => {
									this.setState({ criteria: [criterion] });
								}} />
							) : (
								<ListingSearch
									disableRegistration={true}
									hideSaveButton={true}
									hideStatusField={true}
									user={user}
									onCriteriaChange={(criteria) => this.setState({ criteria })}
									onTermNonActiveListingChange={(value) => this.setState({ isTermNonActiveListing: value })}
									onListingsTotalChange={(listingsTotal) => this.setState({ listingsTotal })}
									hideHeartButton={true}
									hideModal={true}
									nlsOverride={{
										enabled: user.naturalLanguageSearch,
										onlyMode: !!user.naturalLanguageSearch,
										showSavedSearches: false,
										showMarkets: false,
										showSuggestions: !!user.naturalLanguageSearch,
									}}
									nlsSettingsUrl={user.naturalLanguageSearch === false ? "/settings/search/general" : undefined}
								/>
							)}
						</Mui.Grid>
					</Mui.Grid>
				)}
			</FullPageModal>
			</>
		)
}
	}

export const NewMarketDialog = withStyles(Router.withRouter(
	connect(mapStateToProps, mapDispatchToProps)(Component)
), styles);
	